import {
  TeamsReportAnalysisMode,
  TeamsReportClientApp,
  TeamsReportCreatePayload,
  TeamsReportFieldSet,
  TeamsReportNotifyChannel,
  TeamsReportRequest,
  TeamsReportSection,
  TeamsReportStat,
  TeamsReportStatus,
  TeamsReportType,
} from '../../api/reports/teamsReports';
import { SECTION_PRESETS, SectionPresetKey } from './constants';

export interface ReportWizardForm {
  reportType: TeamsReportType;
  /** Snapshot: whole company, or the teams in teamIds. */
  wholeCompany: boolean;
  teamIds: string[];
  stats: TeamsReportStat[];
  /** Individual */
  userIds: string[];
  sections: TeamsReportSection[];
  allQualifications: boolean;
  qualificationTitles: string[];
  fieldSet: TeamsReportFieldSet;
  /** Analysis */
  analysisMode: TeamsReportAnalysisMode;
  requestText: string;
  /** Delivery */
  channels: TeamsReportNotifyChannel[];
  recipientIds: string[];
}

/** Exactly what the gateway's TeamsReportCreate accepts: unknown keys are a 422, so nothing extra. */
export const buildTeamsReportPayload = (
  form: ReportWizardForm,
  clientApp: TeamsReportClientApp
): TeamsReportCreatePayload => {
  const analysis: TeamsReportCreatePayload['analysis'] =
    form.analysisMode === 'preset'
      ? { mode: 'preset', preset: 'building_safety_regulations' }
      : form.analysisMode === 'custom'
      ? { mode: 'custom', request_text: form.requestText.trim() }
      : { mode: 'none' };

  // The requester is always told once a channel is on; extra recipients only make sense then
  const notify = {
    channels: form.channels,
    user_ids: form.channels.length ? form.recipientIds : [],
  };

  if (form.reportType === 'company_snapshot') {
    return {
      report_type: 'company_snapshot',
      client_app: clientApp,
      scope: { team_ids: form.wholeCompany ? null : form.teamIds },
      snapshot: { stats: form.stats },
      analysis,
      notify,
    };
  }

  const withQualifications = form.sections.includes('qualifications');
  return {
    report_type: 'individual',
    client_app: clientApp,
    scope: { user_ids: form.userIds },
    individual: {
      sections: form.sections,
      qualification_titles:
        withQualifications && !form.allQualifications ? form.qualificationTitles : null,
      field_set: form.fieldSet,
    },
    analysis,
    notify,
  };
};

/** The preset whose sections match the selection exactly, if any. */
export const getMatchingPreset = (sections: TeamsReportSection[]): SectionPresetKey | null => {
  const preset = SECTION_PRESETS.find(
    item => item.sections.length === sections.length && item.sections.every(s => sections.includes(s))
  );
  return preset?.key ?? null;
};

export const isReportInProgress = (status: TeamsReportStatus) => status === 'pending' || status === 'running';

export const containsSensitiveSections = (sections: TeamsReportSection[]) =>
  sections.includes('personal') || sections.includes('id_rtw');

/** "Scope: Vyce Squad, Roaming Crew", "Scope: 2 team members" or "Scope: Whole company". */
export const getScopeLabel = (report: Pick<TeamsReportRequest, 'report_type' | 'scope_summary'>) => {
  const { scope_summary: scope } = report;
  if (report.report_type === 'individual') {
    const count = scope.people_count ?? scope.user_ids.length;
    return `${count} team ${count === 1 ? 'member' : 'members'}`;
  }
  if (scope.team_ids === null) return 'Whole company';
  return scope.team_names.length ? scope.team_names.join(', ') : `${scope.team_ids.length} teams`;
};

/** Bytes as MB with one decimal, never "0.0 MB" for a real file. */
export const formatReportSize = (bytes: number | null | undefined) => {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  return `${mb < 0.1 ? '0.1' : mb.toFixed(1)} MB`;
};

export const getReportTitle = (report: Pick<TeamsReportRequest, 'report_type' | 'file_name'>) =>
  report.file_name?.replace(/\.pdf$/i, '') ||
  (report.report_type === 'individual' ? 'Individual report' : 'Company snapshot');

export interface ReportErrorMessage {
  title: string;
  description: string;
}

/**
 * The gateway's own words for a refused request (detail.message.title / description); the
 * description names a permission, never a person. A pydantic 422 is a list of field errors.
 */
export const getReportErrorMessage = (error: any): ReportErrorMessage => {
  const status: number | undefined = error?.response?.status;
  const detail = error?.response?.data?.detail;

  if (Array.isArray(detail)) {
    const first = detail[0];
    const field = Array.isArray(first?.loc) ? first.loc.filter((part: unknown) => part !== 'body').join('.') : '';
    return {
      title: 'Check the report settings',
      description: [field, first?.msg].filter(Boolean).join(': ') || 'Some settings are not valid.',
    };
  }

  const title: string | undefined =
    typeof detail?.message?.title === 'string'
      ? detail.message.title
      : typeof detail?.title === 'string'
      ? detail.title
      : typeof detail === 'string'
      ? detail
      : undefined;
  const description: string =
    (typeof detail?.message?.description === 'string' && detail.message.description) ||
    (typeof detail?.description === 'string' && detail.description) ||
    '';

  if (status === 503) {
    return { title: title || 'Report service unavailable', description: 'Try again in a minute.' };
  }
  if (!status) {
    return {
      title: 'We couldn’t reach Vyce',
      description: 'Check your connection and try again.',
    };
  }
  return {
    title: title || 'We couldn’t request this report',
    description: description || (status === 403 ? 'You don’t have access to do this.' : 'Please try again.'),
  };
};

/** The path from the notification links (no /#/), turned into the hash route the apps use. */
export const getHashRouteForReportPath = (pathname: string): string | null => {
  const match = pathname.match(/^\/teams\/reports\/([0-9a-fA-F-]{36})\/?$/);
  return match ? `/#/teams/reports/${match[1]}` : null;
};
